import { useCallback } from "react";

import { PlotlyChart as Base } from "@corca/tiptap";
import { NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react";
import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { PlotlyRenderer } from "@/components/plotly/PlotlyRenderer";
import { LineWrapper } from "@/components/tiptap/utils/line-wrapper/LineWrapper";
import { getIsMobile } from "@/utils/getIsMobile";

import { BlockResizerControls, BlockResizerRoot } from "./BlockResizer";

import type { DocumentModel } from "@/models/DocumentModel";
import type { PlotlyChartNodeAttrs } from "@corca/tiptap";
import type { NodeViewProps } from "@tiptap/core";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    plotlyChart: {
      appendPlotlyChart: (position?: number, attrs?: Partial<PlotlyChartNodeAttrs>) => ReturnType;
    };
  }
}

type PlotlyChartExtensionOptions = {
  doc: DocumentModel;
};

const PlotlyChartWrapper = observer(function PlotlyChartWrapper({
  node,
  extension,
  selected,
  updateAttributes,
  getPos,
}: NodeViewProps) {
  const t = useTranslations("doc.graph");
  const isMobile = getIsMobile();
  const { doc } = extension.options as PlotlyChartExtensionOptions;
  const attrs = node.attrs as PlotlyChartNodeAttrs;

  const onClick = useCallback(() => {
    const pos = getPos();
    if (pos !== undefined) {
      doc.editor.tiptap?.chain().focus().setNodeSelection(pos).run();
    }
  }, [doc, getPos]);

  return (
    <NodeViewWrapper>
      <LineWrapper doc={doc} getPos={getPos} contentClassName="flex">
        <BlockResizerRoot
          enabled={!isMobile && selected && doc.editable}
          className="sm:w-full [&>button]:sm:hidden"
          onBlockResize={(size) => {
            updateAttributes({ width: size.width, height: size.height });
          }}
        >
          <div className="w-0 h-0">&nbsp;</div>
          <BlockResizerControls getLabel={(index) => t(`resize-dot-label.${index}`, { index })} />
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
          <div
            contentEditable={false}
            onClick={onClick}
            style={{
              width: attrs.width,
              height: attrs.height,
            }}
          >
            <PlotlyRenderer
              data={attrs.plotlyData as Plotly.Data[]}
              layout={attrs.plotlyLayout as Partial<Plotly.Layout>}
              width={attrs.width}
              height={attrs.height}
            />
          </div>
        </BlockResizerRoot>
      </LineWrapper>
    </NodeViewWrapper>
  );
});

export const PlotlyChartPlugin = Base.extend<PlotlyChartExtensionOptions>({
  addNodeView() {
    return ReactNodeViewRenderer(PlotlyChartWrapper, {
      className: "w-full mx-auto max-w-content-width sm:max-w-full",
    });
  },
});
